import { apiBaseUrl } from "./auth";

// One WebSocket per tab for the real-time chat, shared by the sidebar badge,
// the chat box and the messages page. It reconnects by itself; while it is
// down the pages keep polling, so nothing is missed.

const listeners = new Set();
const statusListeners = new Set();
let socket = null;
let token = null;
let users = 0;
let retry = 0;
let retryTimer = null;
let pingTimer = null;
let ready = false;

function socketUrl() {
  const base = String(apiBaseUrl || window.location.origin).replace(/\/+$/, "");
  return `${base.replace(/^http/, "ws")}/ws/messages/`;
}

function setReady(value) {
  if (ready === value) return;
  ready = value;
  statusListeners.forEach((fn) => fn(ready));
}

function open() {
  if (socket || !token || typeof WebSocket === "undefined") return;
  let ws;
  try {
    ws = new WebSocket(socketUrl());
  } catch {
    scheduleRetry();
    return;
  }
  socket = ws;
  ws.onopen = () => ws.send(JSON.stringify({ type: "auth", token }));
  ws.onmessage = (event) => {
    let data;
    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }
    if (data?.type === "ready") {
      retry = 0;
      setReady(true);
      clearInterval(pingTimer);
      // Keeps proxies from closing an idle connection.
      pingTimer = setInterval(() => send({ type: "ping" }), 25000);
      return;
    }
    if (data?.type === "pong") return;
    listeners.forEach((fn) => fn(data));
  };
  ws.onclose = (event) => {
    if (socket === ws) socket = null;
    clearInterval(pingTimer);
    setReady(false);
    // 4401: token refused (expired) — the next connect() brings a fresh one.
    if (event.code !== 4401 && event.code !== 4403) scheduleRetry();
  };
  ws.onerror = () => ws.close();
}

function scheduleRetry() {
  clearTimeout(retryTimer);
  if (!token || !users) return;
  retry = Math.min(retry + 1, 6);
  retryTimer = setTimeout(open, Math.min(30000, 1000 * 2 ** retry));
}

function close() {
  clearTimeout(retryTimer);
  clearInterval(pingTimer);
  const ws = socket;
  socket = null;
  setReady(false);
  if (ws) ws.close(1000);
}

// Call with the current access token; returns a release function.
export function connectChat(accessToken) {
  users += 1;
  if (accessToken !== token) {
    token = accessToken;
    close();
  }
  open();
  return () => {
    users = Math.max(0, users - 1);
    if (!users) {
      token = null;
      close();
    }
  };
}

export function send(payload) {
  if (socket && ready && socket.readyState === WebSocket.OPEN) {
    socket.send(JSON.stringify(payload));
    return true;
  }
  return false;
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function onStatus(listener) {
  statusListeners.add(listener);
  listener(ready);
  return () => statusListeners.delete(listener);
}

export function isReady() {
  return ready;
}
